import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import {
  ShieldCheck,
  ShieldAlert,
  KeyRound,
  Copy,
  Check,
  Loader2,
  RotateCw,
  Trash2,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

// -----------------------------------------------------------------------------
// Postmoro — Security settings page
// -----------------------------------------------------------------------------
// Wires the /api/auth/2fa/* backend into a Settings surface where users can:
//   * Enable TOTP two-factor authentication (scan QR / paste secret, then
//     confirm with a 6-digit code).
//   * View one-time recovery codes generated at enable time.
//   * Regenerate recovery codes (invalidating any unused ones).
//   * Disable 2FA (password + TOTP or recovery code required, server-side).
// -----------------------------------------------------------------------------

function parseSecretFromUri(uri) {
  try {
    const match = /[?&]secret=([^&]+)/i.exec(uri || "");
    return match ? decodeURIComponent(match[1]) : "";
  } catch (_e) {
    return "";
  }
}

function RecoveryCodeGrid({ codes }) {
  const [copiedAll, setCopiedAll] = useState(false);
  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopiedAll(true);
      toast.success("Recovery codes copied to clipboard.");
      setTimeout(() => setCopiedAll(false), 2200);
    } catch (_e) {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  };
  return (
    <div className="mt-4" data-testid="security-recovery-codes">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-medium text-[color:var(--text-1)]">
            Your recovery codes
          </div>
          <div className="text-[11px] text-[color:var(--text-3)] mt-0.5">
            Save these somewhere safe. Each code works ONCE if you lose access
            to your authenticator.
          </div>
        </div>
        <Button
          type="button"
          onClick={handleCopyAll}
          data-testid="security-copy-all-codes"
          variant="outline"
          className="text-xs border-[color:var(--border-subtle)]"
        >
          {copiedAll ? (
            <>
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5 mr-1.5" />
              Copy all
            </>
          )}
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 font-mono text-[13px] tracking-widest text-[color:var(--text-1)]">
        {codes.map((c, i) => (
          <div key={i} data-testid={`security-recovery-code-${i}`} className="py-1">
            {c}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SecurityPage() {
  const [status, setStatus] = useState({
    loading: true,
    enabled: false,
    recovery_codes_remaining: 0,
  });

  // Enrollment flow state
  const [enrolling, setEnrolling] = useState(false);
  const [enrollUri, setEnrollUri] = useState("");
  const [enrollSecret, setEnrollSecret] = useState("");
  const [enrollCode, setEnrollCode] = useState("");
  const [savingEnroll, setSavingEnroll] = useState(false);

  // Codes just generated (either at enable or via regenerate)
  const [freshCodes, setFreshCodes] = useState([]);

  // Regenerate flow state
  const [showRegen, setShowRegen] = useState(false);
  const [regenCode, setRegenCode] = useState("");
  const [regenBusy, setRegenBusy] = useState(false);

  // Disable flow state
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disableRecovery, setDisableRecovery] = useState("");
  const [disableBusy, setDisableBusy] = useState(false);

  const fetchStatus = useCallback(async () => {
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const res = await api.get("/auth/2fa/status");
      setStatus({
        loading: false,
        enabled: !!res?.data?.enabled,
        recovery_codes_remaining: Number(res?.data?.recovery_codes_remaining || 0),
      });
    } catch (err) {
      setStatus({ loading: false, enabled: false, recovery_codes_remaining: 0 });
      toast.error(formatApiError(err, "Couldn't load your 2FA status."));
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const beginEnrollment = async () => {
    setEnrolling(true);
    setFreshCodes([]);
    setEnrollCode("");
    try {
      const res = await api.post("/auth/2fa/setup", {});
      const uri = res?.data?.provisioning_uri || "";
      setEnrollUri(uri);
      setEnrollSecret(parseSecretFromUri(uri));
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't start 2FA setup."));
      setEnrolling(false);
    }
  };

  const cancelEnrollment = () => {
    setEnrolling(false);
    setEnrollUri("");
    setEnrollSecret("");
    setEnrollCode("");
  };

  const confirmEnrollment = async (e) => {
    e?.preventDefault?.();
    if (!enrollCode.trim()) return;
    setSavingEnroll(true);
    try {
      const res = await api.post("/auth/2fa/enable", { code: enrollCode.trim() });
      const codes = res?.data?.recovery_codes || [];
      setFreshCodes(codes);
      setEnrolling(false);
      setEnrollUri("");
      setEnrollSecret("");
      setEnrollCode("");
      toast.success("Two-factor authentication is now on.");
      await fetchStatus();
    } catch (err) {
      toast.error(formatApiError(err, "Invalid or expired verification code."));
    } finally {
      setSavingEnroll(false);
    }
  };

  const submitRegenerate = async (e) => {
    e?.preventDefault?.();
    if (!regenCode.trim()) return;
    setRegenBusy(true);
    try {
      const res = await api.post("/auth/2fa/recovery-codes/regenerate", {
        code: regenCode.trim(),
      });
      setFreshCodes(res?.data?.recovery_codes || []);
      setShowRegen(false);
      setRegenCode("");
      toast.success("New recovery codes generated. Old codes no longer work.");
      await fetchStatus();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't regenerate recovery codes."));
    } finally {
      setRegenBusy(false);
    }
  };

  const submitDisable = async (e) => {
    e?.preventDefault?.();
    if (!disableCode.trim() && !disableRecovery.trim()) {
      toast.error("Enter an authenticator code or a recovery code.");
      return;
    }
    setDisableBusy(true);
    try {
      const body = { password: disablePassword };
      if (disableCode.trim()) body.code = disableCode.trim();
      if (disableRecovery.trim()) body.recovery_code = disableRecovery.trim();
      await api.post("/auth/2fa/disable", body);
      setShowDisable(false);
      setDisablePassword("");
      setDisableCode("");
      setDisableRecovery("");
      setFreshCodes([]);
      toast.success("Two-factor authentication turned off.");
      await fetchStatus();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't disable 2FA."));
    } finally {
      setDisableBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="security-page">
      <PageHeader
        title="Security"
        subtitle="Protect your Postmoro account with two-factor authentication. Recommended for admins and shared team accounts."
        testidPrefix="security"
      />

      <motion.section
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5 sm:p-6"
        data-testid="security-2fa-card"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div
              className={
                "h-10 w-10 rounded-lg flex items-center justify-center " +
                (status.enabled
                  ? "bg-[color:rgba(27,168,159,0.14)] text-[color:var(--brand-primary)]"
                  : "bg-[color:rgba(148,163,184,0.12)] text-[color:var(--text-2)]")
              }
            >
              {status.enabled ? <ShieldCheck className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
            </div>
            <div>
              <div className="text-sm font-semibold text-[color:var(--text-1)]">
                Two-factor authentication
              </div>
              <div className="text-xs text-[color:var(--text-3)] mt-0.5">
                Requires a 6-digit code from your authenticator app on every sign-in.
              </div>
              <div
                className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
                style={
                  status.enabled
                    ? { color: "var(--brand-primary)", background: "rgba(27,168,159,0.12)", border: "1px solid rgba(27,168,159,0.25)" }
                    : { color: "var(--text-3)", background: "var(--surface-2)", border: "1px solid var(--border-subtle)" }
                }
                data-testid="security-2fa-status-pill"
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: status.enabled ? "var(--brand-primary)" : "var(--text-3)" }}
                />
                {status.loading ? "Loading…" : status.enabled ? "Enabled" : "Not enabled"}
              </div>
            </div>
          </div>

          {!status.loading && !status.enabled && !enrolling ? (
            <Button
              type="button"
              onClick={beginEnrollment}
              data-testid="security-enable-2fa-btn"
              className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Enable 2FA
            </Button>
          ) : null}

          {!status.loading && status.enabled ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowRegen((v) => !v); setShowDisable(false); }}
                data-testid="security-regenerate-toggle"
                className="text-xs border-[color:var(--border-subtle)]"
              >
                <RotateCw className="h-3.5 w-3.5 mr-1.5" />
                Regenerate codes
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowDisable((v) => !v); setShowRegen(false); }}
                data-testid="security-disable-toggle"
                className="text-xs border-[color:var(--danger)]/40 text-[color:var(--danger)]"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Disable
              </Button>
            </div>
          ) : null}
        </div>

        {/* Recovery codes remaining note */}
        {status.enabled && !freshCodes.length ? (
          <div
            className="mt-4 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2.5 text-xs text-[color:var(--text-2)]"
            data-testid="security-recovery-remaining"
          >
            You have{" "}
            <span className="text-[color:var(--text-1)] font-medium">
              {status.recovery_codes_remaining}
            </span>{" "}
            unused recovery codes.
            {status.recovery_codes_remaining <= 2 ? (
              <span className="text-[color:var(--warning)] ml-1">
                Consider regenerating your codes soon.
              </span>
            ) : null}
          </div>
        ) : null}

        {/* Enrollment flow */}
        {enrolling ? (
          <form onSubmit={confirmEnrollment} className="mt-5 space-y-4" data-testid="security-enroll-form">
            <div className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-5 flex flex-col sm:flex-row items-center gap-5">
              {enrollUri ? (
                <div className="rounded-lg bg-white p-3" data-testid="security-enroll-qr">
                  <QRCodeSVG value={enrollUri} size={168} includeMargin={false} level="M" />
                </div>
              ) : (
                <div className="h-[192px] w-[192px] rounded-lg bg-[color:var(--surface-1)] flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-[color:var(--text-3)]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[color:var(--text-1)] mb-1">
                  Step 1 · Scan the QR code
                </div>
                <p className="text-xs text-[color:var(--text-3)] leading-relaxed">
                  Open Google Authenticator, 1Password, Authy, or a similar app and add
                  a new account by scanning this code.
                </p>
                {enrollSecret ? (
                  <div className="mt-3">
                    <div className="text-[11px] text-[color:var(--text-3)] mb-1">
                      Can't scan? Enter this secret manually:
                    </div>
                    <div
                      className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] px-2.5 py-1.5 font-mono text-[12px] break-all text-[color:var(--text-1)]"
                      data-testid="security-enroll-secret"
                    >
                      {enrollSecret}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div>
              <label className="text-xs text-[color:var(--text-2)]">
                Step 2 · Enter the 6-digit code from your app
              </label>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="123456"
                value={enrollCode}
                onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, ""))}
                data-testid="security-enroll-code-input"
                className="mt-1.5 max-w-[220px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)] font-mono tracking-[0.3em] text-center text-lg"
                autoFocus
                required
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="submit"
                disabled={savingEnroll || enrollCode.length < 6}
                data-testid="security-enroll-confirm-btn"
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
              >
                {savingEnroll ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enabling…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Confirm and turn on 2FA
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={cancelEnrollment}
                data-testid="security-enroll-cancel-btn"
                className="border-[color:var(--border-subtle)]"
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : null}

        {/* Fresh codes (after enable OR after regenerate) */}
        {freshCodes.length > 0 ? <RecoveryCodeGrid codes={freshCodes} /> : null}

        {/* Regenerate flow */}
        {showRegen ? (
          <form onSubmit={submitRegenerate} className="mt-5 space-y-3" data-testid="security-regen-form">
            <div className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4">
              <div className="text-sm font-medium text-[color:var(--text-1)] mb-1 flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-[color:var(--brand-primary)]" />
                Regenerate recovery codes
              </div>
              <p className="text-xs text-[color:var(--text-3)]">
                Enter a fresh 6-digit code from your authenticator to prove it's you.
                Any old recovery codes will stop working.
              </p>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="123456"
                value={regenCode}
                onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, ""))}
                data-testid="security-regen-code-input"
                className="mt-3 max-w-[220px] bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)] font-mono tracking-[0.3em] text-center"
                autoFocus
                required
              />
              <div className="mt-3 flex items-center gap-2">
                <Button
                  type="submit"
                  disabled={regenBusy || regenCode.length < 6}
                  data-testid="security-regen-submit"
                  className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                >
                  {regenBusy ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating…
                    </>
                  ) : (
                    "Generate new codes"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowRegen(false); setRegenCode(""); }}
                  data-testid="security-regen-cancel"
                  className="border-[color:var(--border-subtle)]"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        ) : null}

        {/* Disable flow */}
        {showDisable ? (
          <form onSubmit={submitDisable} className="mt-5 space-y-3" data-testid="security-disable-form">
            <div className="rounded-lg border border-[color:var(--danger)]/30 bg-[color:rgba(238,90,82,0.05)] p-4 space-y-3">
              <div className="text-sm font-medium text-[color:var(--danger)] flex items-center gap-2">
                <ShieldAlert className="h-4 w-4" />
                Turn off two-factor authentication
              </div>
              <p className="text-xs text-[color:var(--text-3)] leading-relaxed">
                Confirm your password AND a current authenticator code (or a recovery
                code). This weakens your account security — you can turn 2FA back on
                anytime.
              </p>
              <div>
                <label className="text-xs text-[color:var(--text-2)]">Password</label>
                <Input
                  type="password"
                  autoComplete="current-password"
                  placeholder="Your Postmoro password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  data-testid="security-disable-password"
                  className="mt-1.5 bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)]"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">Authenticator code</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="123456"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="security-disable-code"
                    className="mt-1.5 bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)] font-mono tracking-[0.3em] text-center"
                  />
                </div>
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">Recovery code</label>
                  <Input
                    type="text"
                    placeholder="XXXX-XXXX"
                    value={disableRecovery}
                    onChange={(e) => setDisableRecovery(e.target.value.toUpperCase())}
                    data-testid="security-disable-recovery"
                    className="mt-1.5 bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)] font-mono tracking-widest uppercase text-center"
                  />
                </div>
              </div>
              <div className="text-[11px] text-[color:var(--text-3)]">
                Enter either the authenticator code OR a recovery code — not both.
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="submit"
                  disabled={disableBusy}
                  data-testid="security-disable-submit"
                  className="bg-[color:var(--danger)] hover:bg-[color:var(--danger)]/90 text-white font-semibold"
                >
                  {disableBusy ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Turning off…
                    </>
                  ) : (
                    "Turn off 2FA"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowDisable(false);
                    setDisablePassword("");
                    setDisableCode("");
                    setDisableRecovery("");
                  }}
                  data-testid="security-disable-cancel"
                  className="border-[color:var(--border-subtle)]"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        ) : null}
      </motion.section>
    </div>
  );
}
